import { Link } from "react-router-dom";
import { useCart } from "@/App";
import { Button } from "@/components/ui/button";
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from "lucide-react";

export const CartPage = () => {
  const { cart, cartTotal, removeFromCart, clearCart } = useCart();

  if (cart.items.length === 0) {
    return (
      <div className="py-24 px-6 text-center max-w-md mx-auto" data-testid="empty-cart">
        <ShoppingBag className="w-16 h-16 mx-auto text-muted-foreground mb-6" />
        <h1 className="font-serif text-3xl mb-4">Your Cart is Empty</h1>
        <p className="text-muted-foreground mb-8">
          Looks like you haven't added any artwork yet.
        </p>
        <Link to="/shop">
          <Button className="rounded-none px-8 py-3 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold">
            Browse Artwork
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="py-12 md:py-16 px-6 md:px-12 lg:px-24 max-w-screen-2xl mx-auto" data-testid="cart-page">
      <div className="flex items-center justify-between mb-12">
        <div>
          <p className="text-sm uppercase tracking-widest text-muted-foreground mb-2">Your Selection</p>
          <h1 className="font-serif text-4xl md:text-5xl font-normal tracking-tight">Shopping Cart</h1>
        </div>
        <Button
          variant="ghost"
          onClick={clearCart}
          className="text-muted-foreground hover:text-destructive"
          data-testid="clear-cart-btn"
        >
          Clear All
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-6">
          {cart.items.map((item) => (
            <div 
              key={item.artwork_id} 
              className="flex gap-6 pb-6 border-b border-border"
              data-testid={`cart-item-${item.artwork_id}`}
            >
              <img
                src={item.image_url}
                alt={item.title}
                className="w-24 h-32 object-cover"
              />
              <div className="flex-1 flex flex-col">
                <h3 className="font-serif text-lg">{item.title}</h3>
                <p className="text-muted-foreground text-sm mb-auto">Digital Download</p>
                <div className="flex items-center justify-between mt-4">
                  <p className="font-medium">${item.price.toFixed(2)}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFromCart(item.artwork_id)}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid={`remove-item-${item.artwork_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-muted/30 p-6 md:p-8">
            <h2 className="font-serif text-xl mb-6">Order Summary</h2>
            
            <div className="space-y-4 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>${cartTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Delivery</span>
                <span className="text-green-600">Digital - Instant</span>
              </div>
            </div>

            <div className="border-t border-border pt-4 mb-8">
              <div className="flex justify-between font-medium text-lg">
                <span>Total</span>
                <span data-testid="cart-total">${cartTotal.toFixed(2)}</span>
              </div>
            </div>

            <Link to="/checkout">
              <Button 
                className="w-full rounded-none px-8 py-6 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold gap-2"
                data-testid="checkout-btn"
              >
                Proceed to Checkout
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>

            <p className="mt-4 text-xs text-center text-muted-foreground">
              Secure checkout powered by Stripe, PayPal & Razorpay
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
